import React, { useRef, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View, Animated } from "react-native";
import WebPageLayout from "../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../layouts/TablePageLayout";
import { CoreTable } from "../../../../components/CoreTable";
import { DataTable } from "react-native-paper";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { BaseFilters } from "../../../../../web/hooks/useFilters";
import PreventativeMaintenancesListScreenFilters from "./components/PreventativeMaintenancesListScreenFilters";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import useHasPermission from "../../../../../../hooks/useHasPermission";
import { useLazyGetPreventativeMaintenancesQuery } from "../../../../../../store/services/api/preventative-maintenance-api";
import { ApprovalStatus, ApprovalStatusNames, getApprovalStatus } from "../../../../../../store/data/models/approval-status-form";
import { Button as AntButton, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {Button} from "react-native-paper";

interface PreventativeMaintenancesListFilters extends BaseFilters {
  reference?: string;
  description?: string;
  status?: ApprovalStatus[];
  createdOnStartDate?: string;
  createdOnEndDate?: string;
  createdByUserId?: string[];
  approvedByUserId?: string[];
}

export default function PreventativeMaintenancesListScreen() {
  const { hasPermission } = useHasPermission();

  const {
    navigateToCreate,
    navigateToEdit,
    navigateToApproval,
    onSubmitFilter,
    filter,
    tableData,
    isLoading,
  } = useEntityListPage<PreventativeMaintenancesListFilters>({
    entityName: "Preventative Maintenances",
    lazyTableQuery: useLazyGetPreventativeMaintenancesQuery,
    filterDefinition: {
      reference: "",
      sortedColumn: "",
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50,
    },
  });

  // === Filter toggle logic ===
  const [filtersVisible, setFiltersVisible] = useState(true);
  const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;

  const toggleFilters = () => {
    const toValue = filtersVisible ? 0 : 1;
    Animated.timing(filtersWidth, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
    setFiltersVisible(!filtersVisible);
  };

  return (
    <WebPageLayout
      header={{
        previousTab: { title: "Dashboard", routeName: "dashboard", replaceStack: true },
        tabs: [{ title: "Preventative Maintenances", routeName: "list" }],
      }}
    >
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], // adjust width as needed
              }),
              height: "100%",
            }}
          >
            <PreventativeMaintenancesListScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>

            {hasPermission("CanCreatePreventativeMaintenances") && (
              <AntButton size="large" icon={<PlusOutlined />} onClick={navigateToCreate}>
                Create Preventative Maintenance
              </AntButton>
            )}
          </>
        }
      >
        <CoreTable<any>
          headers={[
            { text: "Reference", sortedName: "referenceNumber" },
            { text: "Description", sortedName: "title" },
            { text: "Created By", sortedName: "createdBy" },
            { text: "Approved By", sortedName: "approvedBy" },
            { text: "Status", sortedName: "isApproved" },
            { text: "Edit", flex: 0.5 },
          ]}
          data={tableData}
          keyExtractor={(item) => item.id}
          isLoading={isLoading}
          renderItem={({ item, index }) => {
            const approvalStatus = getApprovalStatus(item);
            const approvalStatusName = ApprovalStatusNames[approvalStatus];
            const showApprovalButton =
              hasPermission("CanApprovePreventativeMaintenances") &&
              approvalStatus === ApprovalStatus.AwaitingApproval;
            const showEditButton =
              hasPermission("CanEditApprovedPreventativeMaintenances") ||
              (hasPermission("CanCreatePreventativeMaintenances") &&
                approvalStatus !== ApprovalStatus.Approved);

            return (
              <CoreTable.Row
                style={index % 2 === 0 ? styles.alternateRow : styles.row}
                key={index}
              >
                <CoreTable.Cell>{item.reference}</CoreTable.Cell>
                <CoreTable.Cell>{item.title}</CoreTable.Cell>
                <CoreTable.Cell>{item.createdBy}</CoreTable.Cell>
                <CoreTable.Cell>{item.approvedBy ?? "N/A"}</CoreTable.Cell>
                <CoreTable.Cell>
                  {approvalStatus === ApprovalStatus.Rejected ? (
                    <Tooltip title={item.rejectedReason ?? "No reason provided"}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                      >
                        <Text>{approvalStatusName}</Text>
                        <Feather name="alert-circle" size={16} color="#fab005" />
                      </View>
                    </Tooltip>
                  ) : (
                    approvalStatusName
                  )}
                </CoreTable.Cell>
                <CoreTable.Cell style={{ flex: 0.5 }}>
                  {showApprovalButton && (
                    <Tooltip title="View for Approval">
                      <TouchableOpacity onPress={() => navigateToApproval(item.id)}>
                        <MaterialIcons name="approval" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showEditButton && (
                    <Tooltip title="Edit">
                      <TouchableOpacity onPress={() => navigateToEdit(item.id)}>
                        <MaterialIcons name="edit" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                </CoreTable.Cell>
              </CoreTable.Row>
            );
          }}
          filters={filter.filters}
          setFilter={filter.setFilter}
        />
      </TablePageLayout>
    </WebPageLayout>
  );
}

const styles = StyleSheet.create({
  row: {
    backgroundColor: "rgba(255,255,255,1)",
  },
  alternateRow: {
    backgroundColor: "rgba(245,245,245,1)",
  },
});
